import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Shield, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Widget compact du solde stickers FNE — destiné au Cockpit CFO.
 */
export default function FneStickerWidget() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/fne/balance`);
      setBalance(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBalance(); }, []);

  const bal = balance?.balance;
  const threshold = balance?.threshold || 100;
  const critical = balance?.critical || 20;
  const env = balance?.environment || 'dev';

  let tone = 'text-emerald-700';
  let bgTone = 'bg-emerald-50 border-emerald-200';
  let alertLabel = null;
  if (bal !== null && bal !== undefined) {
    if (bal <= critical) {
      tone = 'text-rose-700';
      bgTone = 'bg-rose-50 border-rose-300';
      alertLabel = 'CRITIQUE — Recharger immédiatement';
    } else if (bal <= threshold) {
      tone = 'text-amber-700';
      bgTone = 'bg-amber-50 border-amber-300';
      alertLabel = 'Stock bas — Réapprovisionner';
    }
  }

  return (
    <Card className={`${bgTone} border`} data-testid="fne-sticker-widget">
      <CardHeader className="pb-1 pt-3 px-3">
        <CardTitle className="text-xs font-semibold flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-slate-700">
            <Shield className="h-3.5 w-3.5" />
            FNE — Stickers DGI
          </span>
          <Badge variant="outline" className="text-[9px] h-4 px-1.5">
            {env.toUpperCase()}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 px-3 pb-3">
        {loading ? (
          <div className="text-xs text-slate-500">Chargement…</div>
        ) : error ? (
          <div className="text-xs text-rose-600">{error}</div>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <div className={`text-2xl font-bold ${tone}`} data-testid="fne-sticker-balance">
                {bal !== null && bal !== undefined ? bal.toLocaleString('fr-FR') : '—'}
              </div>
              <span className="text-[11px] text-slate-500">stock disponible</span>
            </div>
            {/* Gauge */}
            {bal !== null && bal !== undefined && (
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    bal <= critical ? 'bg-rose-500' : bal <= threshold ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, Math.max(2, (bal / Math.max(threshold * 5, 200)) * 100))}%` }}
                />
              </div>
            )}
            <div className="flex items-center justify-between text-[10px] text-slate-500">
              <span>Alerte : <strong>{threshold}</strong></span>
              <span>Critique : <strong className="text-rose-600">{critical}</strong></span>
            </div>
            {alertLabel && (
              <div className={`flex items-center gap-1 text-[10px] font-medium ${tone}`}>
                <AlertTriangle className="h-3 w-3" />
                {alertLabel}
              </div>
            )}
            {balance?.lastCheckAt && (
              <div className="text-[10px] text-slate-400">
                MAJ {new Date(balance.lastCheckAt).toLocaleString('fr-FR')}
              </div>
            )}
            <div className="flex gap-1.5 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={fetchBalance}
                className="h-6 text-[10px] px-2 flex-1"
                data-testid="fne-refresh-balance-btn"
              >
                <RefreshCw className="h-2.5 w-2.5 mr-1" />
                Rafraîchir
              </Button>
              <Button
                size="sm"
                variant="outline"
                asChild
                className="h-6 text-[10px] px-2 flex-1"
              >
                <a
                  href="https://fne.dgi.gouv.ci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fne-recharge-link"
                >
                  <ExternalLink className="h-2.5 w-2.5 mr-1" />
                  Recharger
                </a>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
